<template>
  <div class="collection-info-section" :class="{'--open': isOpen}">
    <div class="collection-info-section__head" @click="changeOpen">
      <span class="h5 semibold">Товар в коллекциях</span>
      <img src="~/assets/img/svg/common/arrow-down.svg" alt=""/>
    </div>
    <div class="collection-info-section__body">
      <div class="collection-in-product__tags">
        <template
          v-for="(collection, index) in collections"
          :key="`collection-tag-${collection.id}`"
        >
          <nuxt-link
            :to="collection.url"
            :class="{'active': Boolean(index === activeIndexSlide)}"
            target="_blank"
            @click="() => changeSlideIndex(index)"
          >{{ collection.name }}</nuxt-link>
        </template>
      </div>
      <div class="collection-in-product__slider slider-images-navigation-button">
        <client-only>
          <swiper
            :modules="[Navigation, Pagination]"
            :loop="true"
            :navigation="true"
            :pagination="true"
            :space-between="5"
            @swiper="initSwiper"
            @slide-change="changeSlide"
          >
            <swiper-slide
              v-for="(collection, index) in collections"
              :key="`product-image-${collection.id}`"
            >
              <div class="collection-in-product__collection">
                <div class="--image">
                  <nuxt-img
                    :src="String(collection.image_id)"
                    width="557px"
                    height="391px"
                    fit="cover"
                    :alt="`collection-${collection.id}-image-${collection.image_id}`"
                  />
                </div>
              </div>
            </swiper-slide>
          </swiper>
        </client-only>
      </div>

      <div class="collection-in-product__collection">
        <div v-if="selectedCollectionUrl" class="--link">
          <span @click="goToCollectionPage">
            Показать всю коллекцию
          </span>
        </div>
      </div>
      <div class="collection-in-product__free-project">
        <span>Покупая несколько товаров из коллекции - </span>
        <span class="--link" @click="open3dProject">3Д проект бесплатно</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type ComputedRef, onMounted, ref } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Pagination, Navigation } from 'swiper/modules';

const props = defineProps({
  product: {
    type: Object as any,
    default: () => {}
  }
});
const emits = defineEmits(['change']);

const isOpen = ref(true);
const changeOpen = () => {
  isOpen.value = !isOpen.value;
};

// Информация о коллекции
const activeCollection = ref(-1);
const collections = computed(() => {
  return props?.product?.productSetParents || []
});

const refSwiper: Ref<any> = ref(null);
const initSwiper = (_ref: any) => {
  refSwiper.value = _ref;
}
const activeIndexSlide: Ref<number> = ref(0);
const changeSlide = (event: any) => {
  activeIndexSlide.value = event?.realIndex || 0;
};
const changeSlideIndex = (index: number) => {
  refSwiper.value.slideToLoop(index);
};
const changeCollection = (collectionId: number) => {
  activeCollection.value = collectionId;
};
const selectedCollectionUrl: ComputedRef<string> = computed(() => {
  return collections.value?.[activeIndexSlide.value]?.url || ""
});

const goToCollectionPage = async () => {
  await navigateTo( `${selectedCollectionUrl.value}`, {
    open: {
      target: '_blank',
      windowFeatures: {
        noopener: true,
        noreferrer: true,
      }
    }
  })
}

watch(activeIndexSlide, () => {
  const _collectionSlug = collections.value?.[activeIndexSlide.value]?.slug;
  emits('change', _collectionSlug);
});

onMounted(() => {
  const collectionId = collections.value?.[0]?.id || -1;
  changeCollection(collectionId);
});

// 3д проект
const {
  openCreateOrder3dProjectOpen
} = useCreateOrder3dProject();
const open3dProject = () => {
  openCreateOrder3dProjectOpen();
}


</script>

<style lang="scss">
.collection-in-product__tags {
  display: flex;
  flex-wrap: wrap;
  margin-top: -11px;
  margin-left: -11px;

  & > * {
    margin-top: 11px;
    margin-left: 11px;
    border-radius: 7px;
    background-color: #F4F3F1;
    padding: 12px 35px;
    box-sizing: border-box;
    cursor: pointer;

    color: #000;
    font-size: 12px;
    line-height: 100%;
    font-weight: 500;

    &:hover {
      background-color: #DBD2BE;
    }

    &.active {
      background-color: #ECE2C9;
      &:hover {
        background-color: #DBD2BE;
      }
    }
  }
}
.collection-in-product__slider {
  position: relative;
  overflow: hidden;
  margin-top: 16px;
  border-radius: 10px;

  .swiper-button-next, .swiper-button-prev {
    opacity: 0;
  }
  .swiper-pagination-bullet {
    background-color: white;
    opacity: 0;
    width: 5px;
    height: 5px;
  }
  .swiper-pagination-bullet-active {
    opacity: 0;
  }

  &:hover {
    .swiper-pagination-bullet {
      opacity: 0.5;
    }
    .swiper-pagination-bullet-active {
      opacity: 1;
    }
    .swiper-button-next, .swiper-button-prev {
      opacity: 1;
    }
  }
}
.collection-in-product__collection {
  display: flex;
  flex-direction: column;
  user-select: none;

  .--image {
    width: 100%;
    overflow: hidden;
    position: relative;
    border-radius: 10px;
    user-select: none;

    img {
      position: absolute;
      top: 0; left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    &:after {
      content: "";
      float: left;
      padding-top: 70.216%;
    }
  }
  .--link {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    margin-top: 16px;
    cursor: pointer;

    a, span {
      color: #404040;
      font-size: 16px;
      font-weight: 500;
      line-height: 150%;
      text-decoration-line: underline;
      transition: all 0.3s;

      &:hover {
        text-decoration-color: transparent;
      }
    }
  }
}
.collection-in-product__free-project {
  margin-top: 24px;

  color: #989898;
  font-size: 16px;
  font-weight: 500;
  line-height: 150%;

  .--link {
    font-weight: 600;
    text-decoration: underline;
    transition: all 0.2s;
    cursor: pointer;

    &:hover {
      text-decoration-color: transparent;
    }
  }
}

@media (max-width: 1023px) {
  .collection-in-product__tags {
    & > * {
      padding: 12.5px 35px;
    }
  }
  .collection-in-product__slider {
    .swiper-pagination-bullet {
      opacity: 0.5;
    }
    .swiper-pagination-bullet-active {
      opacity: 1;
    }
    .swiper-button-next, .swiper-button-prev {
      opacity: 1;
    }
  }
}
</style>
